import type { Token, TokenResponse, User } from '@unsw/types'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

interface AuthState {
  user: User | null
  tokens: TokenResponse | null
  mode: 'english' | 'business' | undefined
  activeRole: 'teacher' | 'admin' | null
}

interface AuthActions {
  setUser: (user: User | null) => void
  setTokens: (tokens: TokenResponse | null) => void
  setAccessToken: (token: Token) => void
  setMode: (mode: 'english' | 'business' | undefined) => void
  setActiveRole: (role: 'teacher' | 'admin' | null) => void
  toggleRole: () => void
  logout: () => void
  isTokenExpired: (expirationTime: string | null | undefined) => boolean
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  isAccessTokenExpired: () => boolean
  isRefreshTokenExpired: () => boolean
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  mode: undefined,
  activeRole: null,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) => set({ user }),
      setTokens: (tokens) => set({ tokens }),
      setAccessToken: (token) =>
        set((state) => ({
          tokens: state.tokens ? { ...state.tokens, access_token: token } : { access_token: token },
        })),
      setMode: (mode) => set({ mode }),
      setActiveRole: (activeRole) => set({ activeRole }),
      toggleRole: () =>
        set((state) => ({
          activeRole: state.activeRole === 'admin' ? 'teacher' : 'admin',
        })),
      logout: () => set(initialState),

      isTokenExpired: (expirationTime) => {
        if (!expirationTime) return true
        return new Date(expirationTime) < new Date()
      },

      getAccessToken: () => {
        const { tokens } = get()
        return tokens?.access_token?.token ?? null
      },

      getRefreshToken: () => {
        const { tokens } = get()
        return tokens?.refresh_token?.token ?? null
      },

      isAccessTokenExpired: () => {
        const { tokens, isTokenExpired } = get()
        return isTokenExpired(tokens?.access_token?.expiration_time)
      },

      isRefreshTokenExpired: () => {
        const { tokens, isTokenExpired } = get()
        return isTokenExpired(tokens?.refresh_token?.expiration_time)
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        mode: state.mode,
        activeRole: state.activeRole,
      }),
    },
  ),
)

// Selectors
export const selectUser = (state: AuthState) => state.user
export const selectTokens = (state: AuthState) => state.tokens
export const selectIsAuthenticated = (state: AuthState) => !!state.tokens?.access_token
